<template>
  <tx-panel
    ref="refPanel" v-model="panelVisible" :title="t('general.imagesReel')" :width="900" :height="400"
    :close-on-esc="false" :show-minimize="true" :minimize-right="20" :minimize-bottom="70"
    @close="doClose"
  >
    <div class="flex flex-col w-full h-full">
      <!-- TOOLBAR -->
      <div class="w-full h-14 p-2 bg-gray-50 border-b">
        <div class="flex items-center">
          <!-- Title -->
          <div class="ml-1 flex-1 flex items-center gap-2 font-semibold text-md">
            <div class="p-1 border border-solid rounded-md cursor-default text-sky-600 border-sky-600">
              {{ sourceArticles.length }} {{ t('general.articles') }}
            </div>
          </div>
          <div class="flex gap-2">
            <tx-button
              id="generateSlide" v-tooltip="{ text: t('general.generate'), theme: { placement: 'bottom' } }" type="icon" faicon="fa-light fa-presentation-screen"
              @click="onGenerateSlide"
            />
            <tx-select
              v-model="groupByColDivider" :data="getGroupByOptions('col')" value-prop="articleProperty" display-prop="displayLabel"
              filterable clearable :placeholder="t('general.columnDivider')"
            >
              <template #prefix>
                <font-awesome-icon icon="fa-light fa-columns" class="w-4" />
              </template>
            </tx-select>
            <tx-select
              v-model="groupByRowDivider" :data="getGroupByOptions('row')" value-prop="articleProperty" display-prop="displayLabel"
              filterable clearable :placeholder="t('general.rowDivider')"
            >
              <template #prefix>
                <font-awesome-icon icon="fa-light fa-rows" class="w-4" />
              </template>
            </tx-select>
            <tx-button
              id="btnThumbSize" v-tooltip="{ text: t('general.thumbnailSize'), theme: { placement: 'left' } }" type="icon" faicon="fa-light fa-gallery-thumbnails"
              @click="doSwitchThumbSize"
            />
          </div>
        </div>
      </div>
      <div class="p-1 w-full h-full overflow-hidden">
        <div class="w-full h-full overflow-auto">
          <articles-list
            :articles="sourceArticles" :allow-selection="false" :thumbnail-size="thumbnailSize"
            :column-attribute="groupByColDivider" :row-attribute="groupByRowDivider"
            @article-click="onArticleClick" @data-changed="onArticlesListDataChanged"
          />
        </div>
      </div>
    </div>
  </tx-panel>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/userData'
import { useArticleStore } from '@/store/article'
import { useBrowseStore } from '@/store/browse'
import TxPanel from '@/shared/components/TxPanel.vue'
import TxSelect from '@/shared/components/TxSelect.vue'
import TxButton from '@/shared/components/TxButton.vue'
import ArticlesList from '@/shared/components/ArticlesList.vue'
import type MyArticle from '@/models/myArticle'
import { thumbnailConstants } from '@/models/constants'

const emit = defineEmits<{
  (e: 'articleClick', article: MyArticle): void
  (e: 'generate'): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const articleStore = useArticleStore()
const browseStore = useBrowseStore()

const refPanel = ref<InstanceType<typeof TxPanel>>()
const panelVisible = ref(false)
const sourceArticles = ref<MyArticle[]>(articleStore.articles)
const groupByColDivider = ref('')
const groupByRowDivider = ref('')
const thumbnailSize = ref<number>(3)
let groupByColsData = [] as string[]
let groupByRowsData = [] as string[]

function doClose() {
  setVisibility(false)
}

function setVisibility(visible: boolean) {
  panelVisible.value = visible
  groupByColDivider.value = ' '
  nextTick(() => {
    refPanel.value?.doMinimized()
    groupByColDivider.value = ''
  })
  updateArticles(articleStore.articles)
}

function updateArticles(articles: MyArticle[]) {
  sourceArticles.value = articles
}

function getGroupByOptions(dividerCriteria: 'row' | 'col') {
  const res = [] as Array<{ displayLabel: string, articleProperty: string, direction?: string }>
  if (userStore.activeCatalog && userStore.myAttributes) {
    for (let attributeSystemName in userStore.activeCatalog.Config.GroupByAttributes) {
      if (attributeSystemName === '_Segmentation') {
        attributeSystemName = '_Segmentations'
      }
      if (dividerCriteria === 'row') {
        if (userStore.myAttributes.hasOwnProperty(attributeSystemName) && groupByColDivider.value !== attributeSystemName) {
          res.push({ displayLabel: userStore.myAttributes[attributeSystemName].DisplayName, articleProperty: attributeSystemName })
        }
      }
      else {
        if (userStore.myAttributes.hasOwnProperty(attributeSystemName) && groupByRowDivider.value !== attributeSystemName) {
          res.push({ displayLabel: userStore.myAttributes[attributeSystemName].DisplayName, articleProperty: attributeSystemName })
        }
      }
    }
  }
  return res
}

function doSwitchThumbSize() {
  const newSize = thumbnailSize.value <= 1 ? 3 : thumbnailSize.value - 1
  thumbnailSize.value = newSize
}

function onArticleClick(article: MyArticle) {
  emit('articleClick', article)
}

async function onArticlesListDataChanged(articles: MyArticle[], cols: string[], rows: string[]) {
  groupByColsData = cols
  groupByRowsData = rows
}

function onGenerateSlide() {
  const dataObjectValue = {
    isBrowseByAttribute: false,
    articles: [...sourceArticles.value],
    rowDivider: groupByRowDivider.value,
    columnDivider: groupByColDivider.value,
    thumbnailSize: thumbnailConstants.thumbnailSizeNumberToStrEnum[thumbnailSize.value],
    numberOfColumns: groupByColsData.length,
    rows: groupByRowsData,
    viewName: null,
    browseByAttribute: '',
    columns: groupByColsData,
    bucketData: null,
    title: '',
  }
  browseStore.setDataObjectForMerch(dataObjectValue)
  emit('generate')
}

defineExpose({
  visible: panelVisible,
  setVisibility,
  updateArticles,
})
</script>
